/**
 * Main Feedback SDK Client
 */

import type {
  ClientOptions,
  FeedbackData,
  FeedbackResponse,
  EventData,
  EventResponse,
  EventsResponse,
  RequestOptions,
  RetryOptions,
  FeedbackFilters,
  FeedbackListResponse,
  EventFilters,
  EventListResponse,
  UpdateFeedbackStatusData,
  AppListResponse,
  CreateAppData,
  UpdateAppData,
  App,
  Feedback,
  ChangePasswordData,
  CreateUserData,
  BootstrapAdminData,
  User,
  UsersListFilters,
  UsersListResponse,
  UpdateUserData,
} from "./types.js";
import { AuthClient } from "./auth.js";
import { signRequest, generateNonce, getCurrentTimestamp } from "./crypto.js";
import { NetworkError, parseErrorResponse } from "./errors.js";
import { withRetry } from "./utils.js";

/**
 * Feedback System SDK Client
 *
 * @example
 * const client = new FeedbackClient({
 *   baseUrl: 'https://api.example.com',
 *   appCode: 'my-app',
 *   appSecret: 'secret_xxxxx'
 * });
 *
 * // Submit feedback
 * const feedback = await client.submitFeedback({
 *   message: 'Great product!',
 *   email: 'user@example.com'
 * });
 *
 * // Send event
 * await client.sendEvent({
 *   type: 'page_view',
 *   message: 'User viewed homepage'
 * });
 *
 * // Login (for admin APIs)
 * await client.auth.login({
 *   email: 'admin@example.com',
 *   password: 'password'
 * });
 */
export class FeedbackClient {
  private baseUrl: string;
  private appCode: string;
  private appSecret: string;
  private timeout: number;
  private retryOptions: RetryOptions;
  public auth: AuthClient;

  constructor(options: ClientOptions) {
    // Validate required options
    if (!options.baseUrl) {
      throw new Error("baseUrl is required");
    }
    if (!options.appCode) {
      throw new Error("appCode is required");
    }
    if (!options.appSecret) {
      throw new Error("appSecret is required");
    }

    // Remove trailing slash from baseUrl
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.appCode = options.appCode;
    this.appSecret = options.appSecret;
    this.timeout = options.timeout || 30000;

    this.retryOptions = {
      maxRetries: options.maxRetries ?? 3,
      retryDelay: options.retryDelay ?? 1000,
      maxRetryDelay: options.maxRetryDelay ?? 30000,
    };

    // Initialize auth client
    this.auth = new AuthClient(this.makeRequest.bind(this));
  }

  /**
   * Submit feedback
   *
   * @param data - Feedback data
   * @returns Feedback ID
   *
   * @example
   * const result = await client.submitFeedback({
   *   message: 'Bug in checkout page',
   *   email: 'user@example.com',
   *   page: '/checkout',
   *   tags: ['bug', 'urgent']
   * });
   * console.log(result.id);
   */
  async submitFeedback(data: FeedbackData): Promise<FeedbackResponse> {
    return this.makeRequest("/api/v1/feedback", {
      method: "POST",
      body: data,
    });
  }

  /**
   * Send a single event
   *
   * @param data - Event data
   * @returns Event ID
   *
   * @example
   * await client.sendEvent({
   *   type: 'button_click',
   *   level: 'info',
   *   message: 'User clicked submit button',
   *   context: { button_id: 'submit-btn' }
   * });
   */
  async sendEvent(data: EventData): Promise<EventResponse> {
    return this.makeRequest("/api/v1/events", {
      method: "POST",
      body: data,
    });
  }

  /**
   * Send multiple events in batch
   *
   * @param data - Array of event data
   * @returns Array of event IDs
   *
   * @example
   * const result = await client.sendEvents([
   *   { type: 'page_view', message: 'Homepage viewed' },
   *   { type: 'click', message: 'Button clicked' }
   * ]);
   * console.log(result.ids);
   */
  async sendEvents(data: EventData[]): Promise<EventsResponse> {
    return this.makeRequest("/api/v1/events/batch", {
      method: "POST",
      body: { events: data },
    });
  }

  // ==================== Admin APIs ====================

  /**
   * Get feedback list (requires authentication)
   *
   * @param filters - Filter and pagination parameters
   * @returns Paginated feedback list
   *
   * @example
   * const result = await client.getFeedbackList({
   *   app_code: 'my-app',
   *   status: 'new',
   *   page: 1,
   *   page_size: 20
   * });
   * console.log(result.items);
   */
  async getFeedbackList(
    filters?: FeedbackFilters
  ): Promise<FeedbackListResponse> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.app_code) params.append("app_code", filters.app_code);
      if (filters.status) params.append("status", filters.status);
      if (filters.q) params.append("q", filters.q);
      if (filters.from) params.append("from", filters.from);
      if (filters.to) params.append("to", filters.to);
      if (filters.page) params.append("page", filters.page.toString());
      if (filters.page_size)
        params.append("page_size", filters.page_size.toString());
      if (filters.order) params.append("order", filters.order);
    }

    const queryString = params.toString();
    const path = `/api/v1/feedback${queryString ? `?${queryString}` : ""}`;

    return this.makeRequest(path, {
      method: "GET",
      skipAuth: true, // Skip HMAC, use JWT only
    });
  }

  /**
   * Update feedback status (requires admin authentication)
   *
   * @param id - Feedback ID
   * @param data - Status update data
   * @returns Updated feedback
   *
   * @example
   * const updated = await client.updateFeedbackStatus('123', {
   *   status: 'resolved',
   *   tags: ['bug', 'fixed']
   * });
   */
  async updateFeedbackStatus(
    id: string,
    data: UpdateFeedbackStatusData
  ): Promise<Feedback> {
    return this.makeRequest(`/api/v1/feedback/${id}/status`, {
      method: "PATCH",
      body: data,
      skipAuth: true, // Skip HMAC, use JWT only
    });
  }

  /**
   * Get events list (requires authentication)
   *
   * @param filters - Filter and pagination parameters
   * @returns Paginated events list
   *
   * @example
   * const result = await client.getEventsList({
   *   app_code: 'my-app',
   *   type: 'page_view',
   *   level: 'info',
   *   page: 1
   * });
   */
  async getEventsList(filters?: EventFilters): Promise<EventListResponse> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.app_code) params.append("app_code", filters.app_code);
      if (filters.type) params.append("type", filters.type);
      if (filters.level) params.append("level", filters.level);
      if (filters.q) params.append("q", filters.q);
      if (filters.from) params.append("from", filters.from);
      if (filters.to) params.append("to", filters.to);
      if (filters.page) params.append("page", filters.page.toString());
      if (filters.page_size)
        params.append("page_size", filters.page_size.toString());
      if (filters.order) params.append("order", filters.order);
    }

    const queryString = params.toString();
    const path = `/api/v1/events${queryString ? `?${queryString}` : ""}`;

    return this.makeRequest(path, {
      method: "GET",
      skipAuth: true, // Skip HMAC, use JWT only
    });
  }

  /**
   * Get apps list (requires authentication)
   *
   * @param enabled - Filter by enabled status (optional)
   * @returns App list
   *
   * @example
   * const result = await client.getAppsList();
   * console.log(result.items);
   *
   * // Filter enabled apps only
   * const enabled = await client.getAppsList(true);
   */
  async getAppsList(enabled?: boolean): Promise<AppListResponse> {
    const params = new URLSearchParams();
    if (enabled !== undefined) {
      params.append("enabled", enabled.toString());
    }

    const queryString = params.toString();
    const path = `/api/v1/apps${queryString ? `?${queryString}` : ""}`;

    return this.makeRequest(path, {
      method: "GET",
      skipAuth: true, // Skip HMAC, use JWT only
    });
  }

  /**
   * Get app detail (requires admin authentication)
   * @param code - App code
   */
  async getApp(code: string): Promise<App> {
    return this.makeRequest(`/api/v1/apps/${code}`, {
      method: "GET",
      skipAuth: true, // Skip HMAC, use JWT only
    });
  }

  /**
   * Create a new app (requires admin authentication)
   *
   * @param data - App creation data
   * @returns Created app
   *
   * @example
   * const app = await client.createApp({
   *   code: 'new-app',
   *   name: 'New Application',
   *   description: 'My new app',
   *   enabled: true
   * });
   */
  async createApp(data: CreateAppData): Promise<App> {
    return this.makeRequest("/api/v1/apps", {
      method: "POST",
      body: data,
      skipAuth: true, // Skip HMAC, use JWT only
    });
  }

  /**
   * Update an app (requires admin authentication)
   *
   * @param code - App code
   * @param data - App update data
   * @returns Updated app
   *
   * @example
   * const app = await client.updateApp('my-app', {
   *   name: 'Updated Name',
   *   enabled: false
   * });
   *
   * // Rotate signing secret
   * await client.updateApp('my-app', {
   *   rotate_key: true
   * });
   */
  async updateApp(code: string, data: UpdateAppData): Promise<App> {
    return this.makeRequest(`/api/v1/apps/${code}`, {
      method: "PATCH",
      body: data,
      skipAuth: true, // Skip HMAC, use JWT only
    });
  }

  /**
   * Change password (requires authentication)
   *
   * @param data - Password change data
   *
   * @example
   * await client.changePassword({
   *   old_password: 'oldpass123',
   *   new_password: 'newpass456'
   * });
   */
  async changePassword(data: ChangePasswordData): Promise<void> {
    await this.makeRequest("/api/v1/auth/password", {
      method: "POST",
      body: data,
      skipAuth: true, // Skip HMAC, use JWT only
    });
  }

  /**
   * Create a new user (requires admin authentication)
   *
   * @param data - User creation data
   * @returns Created user ID
   *
   * @example
   * const result = await client.createUser({
   *   email: 'viewer@example.com',
   *   username: 'viewer1',
   *   password: 'password123',
   *   role: 'viewer'
   * });
   */
  async createUser(data: CreateUserData): Promise<{ user: User }> {
    return this.makeRequest("/api/v1/auth/users", {
      method: "POST",
      body: data,
      skipAuth: true, // Skip HMAC, use JWT only
    });
  }

  /**
   * List users (requires admin authentication)
   */
  async getUsersList(filters?: UsersListFilters): Promise<UsersListResponse> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.q) params.append("q", filters.q);
      if (filters.page) params.append("page", String(filters.page));
      if (filters.page_size)
        params.append("page_size", String(filters.page_size));
      if (filters.order) params.append("order", filters.order);
    }
    const qs = params.toString();
    const path = `/api/v1/auth/users${qs ? `?${qs}` : ""}`;
    return this.makeRequest(path, { method: "GET", skipAuth: true });
  }

  /** Update user fields */
  async updateUser(id: string, data: UpdateUserData): Promise<{ user: User }> {
    return this.makeRequest(`/api/v1/auth/users/${id}`, {
      method: "PATCH",
      body: data,
      skipAuth: true,
    });
  }

  /** Reset user password */
  async resetUserPassword(
    id: string,
    password: string
  ): Promise<{ message: string }> {
    return this.makeRequest(`/api/v1/auth/users/${id}/reset-password`, {
      method: "POST",
      body: { password },
      skipAuth: true,
    });
  }

  /** Delete user */
  async deleteUser(id: string): Promise<{ message: string }> {
    return this.makeRequest(`/api/v1/auth/users/${id}`, {
      method: "DELETE",
      skipAuth: true,
    });
  }

  /**
   * Bootstrap the first admin user
   *
   * @param data - Admin user data
   * @param bootstrapSecret - Bootstrap secret from environment
   * @returns Login-like response with user and token
   *
   * @example
   * const result = await client.bootstrapAdmin(
   *   {
   *     email: 'admin@example.com',
   *     username: 'admin',
   *     password: 'securepass123'
   *   },
   *   'bootstrap-secret-xyz'
   * );
   */
  async bootstrapAdmin(
    data: BootstrapAdminData,
    bootstrapSecret: string
  ): Promise<{ user: User; token: string }> {
    return this.makeRequest("/api/v1/auth/bootstrap", {
      method: "POST",
      body: data,
      headers: {
        "X-Bootstrap-Secret": bootstrapSecret,
      },
      skipAuth: true, // Skip HMAC, use bootstrap secret header
    });
  }

  /**
   * Export feedback as CSV (requires authentication)
   *
   * @param filters - Filter parameters (same as getFeedbackList)
   * @returns CSV blob
   *
   * @example
   * const csv = await client.exportFeedbackCSV({
   *   app_code: 'my-app',
   *   status: 'new'
   * });
   *
   * // In browser: trigger download
   * const url = URL.createObjectURL(csv);
   * const a = document.createElement('a');
   * a.href = url;
   * a.download = 'feedback.csv';
   * a.click();
   */
  async exportFeedbackCSV(filters?: FeedbackFilters): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.app_code) params.append("app_code", filters.app_code);
      if (filters.status) params.append("status", filters.status);
      if (filters.q) params.append("q", filters.q);
      if (filters.from) params.append("from", filters.from);
      if (filters.to) params.append("to", filters.to);
    }

    const queryString = params.toString();
    const path = `/api/v1/feedback/export.csv${
      queryString ? `?${queryString}` : ""
    }`;

    return this.makeRequestBlob(path, {
      method: "GET",
      skipAuth: true, // Skip HMAC, use JWT only
    });
  }

  /**
   * Export events as CSV (requires authentication)
   *
   * @param filters - Filter parameters (same as getEventsList)
   * @returns CSV blob
   *
   * @example
   * const csv = await client.exportEventsCSV({
   *   app_code: 'my-app',
   *   type: 'error'
   * });
   */
  async exportEventsCSV(filters?: EventFilters): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.app_code) params.append("app_code", filters.app_code);
      if (filters.type) params.append("type", filters.type);
      if (filters.level) params.append("level", filters.level);
      if (filters.q) params.append("q", filters.q);
      if (filters.from) params.append("from", filters.from);
      if (filters.to) params.append("to", filters.to);
    }

    const queryString = params.toString();
    const path = `/api/v1/events/export.csv${
      queryString ? `?${queryString}` : ""
    }`;

    return this.makeRequestBlob(path, {
      method: "GET",
      skipAuth: true, // Skip HMAC, use JWT only
    });
  }

  // ==================== Internal Methods ====================

  /**
   * Make HTTP request with automatic HMAC signing and retry logic
   * @internal
   */
  private async makeRequest(
    path: string,
    options: RequestOptions
  ): Promise<any> {
    return withRetry(async () => {
      const { method, body, headers = {}, skipAuth = false } = options;

      // Prepare request body
      const bodyString = body ? JSON.stringify(body) : null;

      // Build request headers
      const requestHeaders: Record<string, string> = {
        "Content-Type": "application/json",
        ...headers,
      };

      // Add HMAC authentication headers (unless skipped)
      if (!skipAuth) {
        const timestamp = getCurrentTimestamp();
        const nonce = generateNonce();
        const signature = await signRequest(
          method,
          path,
          bodyString,
          timestamp,
          nonce,
          this.appSecret
        );

        requestHeaders["X-App-Code"] = this.appCode;
        requestHeaders["X-Timestamp"] = timestamp.toString();
        requestHeaders["X-Nonce"] = nonce;
        requestHeaders["X-Signature"] = signature;
      }

      // Add JWT token if available
      const token = this.auth.getToken();
      if (token) {
        requestHeaders["Authorization"] = `Bearer ${token}`;
      }

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      try {
        // Make request
        const response = await fetch(`${this.baseUrl}${path}`, {
          method,
          headers: requestHeaders,
          body: bodyString,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Handle error responses
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw parseErrorResponse(response, data);
        }

        // Parse successful response
        return await response.json();
      } catch (error: any) {
        clearTimeout(timeoutId);

        // Handle network errors
        if (error.name === "AbortError") {
          throw new NetworkError(`Request timeout after ${this.timeout}ms`);
        }
        if (error instanceof TypeError && error.message.includes("fetch")) {
          throw new NetworkError("Network request failed");
        }

        // Re-throw other errors
        throw error;
      }
    }, this.retryOptions);
  }

  /**
   * Make HTTP request for blob/binary responses (e.g., CSV export)
   * @internal
   */
  private async makeRequestBlob(
    path: string,
    options: RequestOptions
  ): Promise<Blob> {
    return withRetry(async () => {
      const { method, headers = {} } = options;

      // Build request headers
      const requestHeaders: Record<string, string> = {
        ...headers,
      };

      // Add JWT token if available (required for CSV export)
      const token = this.auth.getToken();
      if (token) {
        requestHeaders["Authorization"] = `Bearer ${token}`;
      }

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      try {
        // Make request
        const response = await fetch(`${this.baseUrl}${path}`, {
          method,
          headers: requestHeaders,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Handle error responses
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw parseErrorResponse(response, data);
        }

        // Return blob
        return await response.blob();
      } catch (error: any) {
        clearTimeout(timeoutId);

        // Handle network errors
        if (error.name === "AbortError") {
          throw new NetworkError(`Request timeout after ${this.timeout}ms`);
        }
        if (error instanceof TypeError && error.message.includes("fetch")) {
          throw new NetworkError("Network request failed");
        }

        // Re-throw other errors
        throw error;
      }
    }, this.retryOptions);
  }
}
